import type { ExistingPatient } from "../existingPatient";
import type { ExistingPatientResult } from "api/responses";
import type { NiceModalHandler } from "@ebay/nice-modal-react";
import type { StrictlyBasisPoints } from "@vyne-shared/utils/currency";

/** Unlinked P/RP in need of a merge before proceeding. */
export interface NeedsMergePatient extends NonNullable<ExistingPatient> {
    unlinked: true;
    id: number;
    balance: StrictlyBasisPoints;
}

/** Determine if value is an unlinked P/RP in need of a merge before proceeding. */
export function isNeedsMerge(existingPrp: ExistingPatient): existingPrp is NeedsMergePatient {
    return existingPrp?.unlinked === true && typeof existingPrp.id === "number";
}

/** Properties specific to a Merge flow */
export interface MergeFlowModalProps {
    readonly modal: NiceModalHandler;
    readonly unlinkedPrp: NeedsMergePatient;
    readonly onSelectExistingPrp: (prp: ExistingPatientResult) => void;
}

/** Merge flow state */
export interface MergeState {
    pmsId?: string;
    confirmedInitial: boolean;
    confirmedFinal: boolean;
    mergedPrp: ExistingPatient;
}

/** Properties to configure the Merge Form. */
export interface MergeFlowProps {
    /** Merge flow state. Represents where we're at in the process. */
    readonly mergeState: MergeState;
    /** Invoked to advance the merge flow. */
    readonly dispatchMergeAction: DispatchMergeAction;
    /** Unlinked patient, that's going to be merged with a PMS patient. */
    readonly unlinkedPrp: NeedsMergePatient;
    /** Message to display in UI when merge fails. */
    readonly mergeFailedMessage: string;
}

/** Action representing selection of a PMS patient to merge with the unlinked patient. */
export interface MergePmsPatientAction {
    type: "pmspatient";
    pmsId: string;
}

/** Action representing click of a 'Merge' button used to advance the merge flow. */
export interface MergeMergeAction {
    type: "merge";
}

/** Action representing completed merge. */
export interface MergeMergedAction {
    type: "merged";
    mergedPrp: Readonly<ExistingPatientResult>;
}

/** Any of the above merge flow actions. */
export type MergeAction = MergeMergeAction | MergePmsPatientAction | MergeMergedAction;

/** Invoked by MergeForm sub-components to advance the merge flow. */
export type DispatchMergeAction = React.Dispatch<MergeAction>;

/** Properties of the form used to search for a PMS patient to merge with the unlinked patient. */
export interface MergeSearchProps {
    /** Unlinked P/RP that we're about to merge with a PMS patient. */
    readonly unlinkedPrp: NeedsMergePatient;
    /** Invoked by the MergeFormSearch to indicate the user has selected a PMS patient to merge with. */
    readonly dispatchMergeAction: DispatchMergeAction;
}

/** Properties of an intermediate confirmation merge form. */
export interface MergeConfirmationProps {
    /** Unlinked P/RP that we're about to merge with a PMS patient. */
    readonly unlinkedPrp: NeedsMergePatient;
    /** PMS patient that we'll link to the unlinked P/RP. */
    readonly pmsId: string;
}

/** Properties of the final confirmation merge form, from which the actual merge can be dispatched. */
export interface MergeMergeProps {
    /** PMS patient that we'll linked to the unlinked P/RP. */
    readonly pmsId: string;
    /** Message to display in UI when merge fails. */
    readonly mergeFailedMessage: string;
}
